import { localDb } from '@/data/local/database'
import type { PendingOperation } from '@/domain/types'
import { isNativePlatform } from '@/platform/platform'
import { localStoreMirror } from '@/platform/local-store'
import { nativeSync } from '@/platform/native-sync'

let installed = false

// Keep the Room queue in lockstep with Dexie. Dexie stays the UI source of
// truth; Room is the authority the background worker reads when the WebView dies.
export function installPendingOperationMirror() {
  if (installed || !isNativePlatform()) return
  installed = true

  localDb.pendingOperations.hook('creating', (_key, operation) => {
    queueMicrotask(() => {
      void localStoreMirror.enqueue([operation as PendingOperation])
      void nativeSync.requestSync()
    })
  })
  localDb.pendingOperations.hook('updating', (mods, _key, operation) => {
    const merged = { ...(operation as PendingOperation), ...(mods as Partial<PendingOperation>) }
    queueMicrotask(() => void localStoreMirror.enqueue([merged]))
    return undefined
  })
  localDb.pendingOperations.hook('deleting', key => {
    queueMicrotask(() => void localStoreMirror.remove([String(key)]))
  })
}

// Full reconciliation at startup: Room mirrors exactly what Dexie holds, and
// conflicts detected by the background worker are surfaced into Dexie.
export async function reconcilePendingOperationMirror(workspaceId: string) {
  if (!isNativePlatform()) return
  const operations = await localDb.pendingOperations.where('workspaceId').equals(workspaceId).toArray()
  await localStoreMirror.replaceAll(workspaceId, operations)
  const nativeConflicts = await localStoreMirror.drainConflicts()
  for (const conflict of nativeConflicts) {
    const existing = await localDb.conflicts.get(conflict.id)
    if (!existing) await localDb.conflicts.put(conflict)
    // The worker already removed the operation from Room; drop the Dexie copy too.
    await localDb.pendingOperations.delete(conflict.operationId)
  }
  if (nativeConflicts.length) {
    window.dispatchEvent(new CustomEvent('study-tool:sync-request'))
  }
}
